<template>
  <v-sheet>
    <v-row>
      <v-spacer></v-spacer>
      <v-col cols="2">
        <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            outlined
            dense
            clearable
            single-line
            hide-details
            class="ma-2"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-sheet outlined style="min-height: 200px; max-height: 800px; overflow-y: auto;">
      <v-data-table
          :headers="dynamicHeaders"
          :items="processedData"
          :search="search"
          :loading="loading"
          dense
          item-key="_uniqueKey"
          fixed-header
          class="sticky-table"
          :footer-props="{
            'items-per-page-options': [10, 20, 50, 100, -1],
            'show-current-page': true,
            'show-first-last-page': true
          }"
          no-data-text="No results available. Please initiate a search by selecting 'Search Items' to navigate the database."
          no-results-text="No results found for the current search terms. Please try different keywords."
      >
        <template v-slot:item.id="{ item }">
          <router-link :to="`/single-search/${item.internal_id}?ptm_type=${encodeURIComponent(item.ptm_type)}`" target="_blank">
            {{ item.id }}
          </router-link>
        </template>

        <template v-slot:item.publication_score="{ item }">
          <span>{{ isFinite(Number(item.publication_score)) ? Number(item.publication_score).toFixed(3) : '-' }}</span>
        </template>

        <template v-slot:item.data_score="{ item }">
          <span>{{ isFinite(Number(item.data_score)) ? Number(item.data_score).toFixed(3) : '-' }}</span>
        </template>

        <template v-slot:item.user_input_score="{ item }">
          <span>{{ isFinite(Number(item.user_input_score)) ? Number(item.user_input_score).toFixed(3) : '-' }}</span>
        </template>

        <template v-slot:item.text_evidence_count="{ item }">
          <v-chip
              v-if="item.text_evidence_count !== 0"
              :color="$store.state.textEvidenceColor"
              outlined
              small
          >
            {{ item.text_evidence_count }}
          </v-chip>
          <span v-else>-</span>
        </template>

        <template v-slot:item.figure_evidence_count="{ item }">
          <v-chip
              v-if="item.figure_evidence_count !== 0"
              :color="$store.state.figureEvidenceColor"
              outlined
              small
          >
            {{ item.figure_evidence_count }}
          </v-chip>
          <span v-else>-</span>
        </template>

        <template v-slot:item.experiment_evidence_count="{ item }">
          <v-chip
              v-if="item.experiment_evidence_count !== 0"
              :color="$store.state.experimentEvidenceColor"
              outlined
              small
          >
            {{ item.experiment_evidence_count }}
          </v-chip>
          <span v-else>-</span>
        </template>

        <template v-slot:item.cohort_evidence_count="{ item }">
          <v-chip
              v-if="item.cohort_evidence_count !== 0"
              :color="$store.state.cohortEvidenceColor"
              outlined
              small
          >
            {{ item.cohort_evidence_count }}
          </v-chip>
          <span v-else>-</span>
        </template>
        <template v-slot:item.encoded_summary="{ item }">
          <v-btn
              v-if="item.has_summary"
              x-small
              color="primary"
              @click="openSummaryDialog(item)"
          >
            View
          </v-btn>
          <span v-else>-</span>
        </template>
      </v-data-table>
      <v-dialog
          v-model="summaryDialog"
          max-width="700"
      >
        <v-card>
          <v-card-title class="headline">
            <div class="d-flex align-center">
              <span class="mr-2">GPT Summary for</span>
              <router-link
                  :to="`/single-search/${currentItemInternal_id}?ptm_type=${encodeURIComponent(currentPtm_type)}`"
                  target="_blank"
              >
                {{ currentItemId }}
              </router-link>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                      icon
                      x-small
                      class="ml-2"
                      :color="copySuccess ? 'success' : 'default'"
                      @click="copySummary"
                      v-bind="attrs"
                      v-on="on"
                  >
                    <v-icon small>{{ copySuccess ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ copySuccess ? 'Copied!' : 'Copy summary' }}</span>
              </v-tooltip>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon @click="summaryDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4">
            <div class="summary-content">
              {{ currentSummary }}
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-sheet>
  </v-sheet>
</template>

<script>
export default {
  name: "BatchTable",
  props: {
    tdata: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      search: "",
      summaryDialog: false,
      currentSummary: "",
      currentItemId: "",
      currentItemInternal_id:'',
      currentPtm_type:'',
      copySuccess: false
    };
  },

  computed: {
    processedData() {
      return this.tdata.map((item, index) => ({
        ...item,
        _uniqueKey: `${item.internal_id}__${item.ptm_type}__${item.user_input_score ?? 'null'}`
      }));
    },

    dynamicHeaders() {
      const data = Array.isArray(this.tdata) ? this.tdata : [];
      const hasUserInput = data.some(item => item.user_input_score != null);

      const baseHeaders = [
        { text: "ID", value: "id", align: "start", sortable: true, width: "9%" },
        { text: "Publication Score", value: "publication_score", width: "8%", sortable: true },
        { text: "Data Score", value: "data_score", width: "7%", sortable: true },
        { text: "#Total", value: "evidence_total", width: "5%", sortable: true },
        { text: "#Text mining", value: "text_evidence_count", width: "6%", sortable: true },
        { text: "#Figure mining", value: "figure_evidence_count", width: "6%", sortable: true },
        { text: "#PTMAtlas datasets", value: "experiment_evidence_count", width: "8%", sortable: true },
        { text: "#CPTAC datasets", value: "cohort_evidence_count", width: "8%", sortable: true },
        { text: "GPT summary",value:'encoded_summary', width: "8%", sortable: false },
      ];

      if (hasUserInput) {
        baseHeaders.splice(3, 0, {
          text: "User Input Score",
          value: "user_input_score",
          align: "start",
          sortable: true,
          width: "8%"
        });
      }

      return baseHeaders;
    }
  },
  methods: {
    openSummaryDialog(item) {
      if (item.encoded_summary) {
        try {
          this.currentSummary = atob(item.encoded_summary);
          this.currentItemId = item.id;
          this.currentItemInternal_id = item.internal_id;
          this.currentPtm_type = item.ptm_type;
          this.copySuccess = false;
          this.summaryDialog = true;
        } catch (e) {
          console.error('Failed to decode summary:', e);
          this.currentSummary = 'Failed to load summary';
          this.currentItemId = item.id;
          this.summaryDialog = true;
        }
      }
    },

    copySummary() {
      navigator.clipboard.writeText(this.currentSummary).then(() => {
        this.copySuccess = true;

        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      }).catch(err => {
        console.error('Failed to copy:', err);
        this.fallbackCopy(this.currentSummary);
      });
    },

    fallbackCopy(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.copySuccess = true;
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  },


  // watch: {
  //   tdata: {
  //     handler(newVal) {
  //       console.log('[BatchTable] Data updated:', newVal.length, 'items');
  //     }
  //   }
  // }
};
</script>

<style scoped>
.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  color: black !important;
  font-size: 0.8rem;
  white-space: nowrap;
}

.sticky-table table {
  table-layout: fixed;
  width: 100%;
}

.sticky-table thead tr th:first-of-type {
  position: sticky;
  left: 0;
  background: white;
  z-index: 2;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  min-width: 120px;
  max-width: 200px;
  white-space: nowrap;
}

.sticky-table tbody td:first-child {
  position: sticky;
  left: 0;
  background: white;
  z-index: 0;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}
.headline {
  background-color: #f5f5f5;
}
</style>